import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { EntityImage } from '../components/EntityImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Boxes, Plus, Minus, Trash2, Upload, X, DollarSign, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_IMAGES_PER_ITEM, canAddMoreImages, getImageCountDisplay, validateImageUpload } from '../lib/imageLimit';

const COMMON_CALIBERS = ['9mm', '.45 ACP', '.22 LR', '5.56 NATO', '.308 WIN', '12 Gauge', '.40 S&W', '.380 ACP', '7.62x39', '.223 REM'];
const AMMO_TYPES = ['FMJ', 'JHP', 'HP', 'SP', 'Buckshot', 'Slug', 'Birdshot', 'Match', 'Tracer', 'Other'];

export default function AmmunitionFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const isDetail = isEdit && !window.location.pathname.includes('/edit');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [ammo, setAmmo] = useState(null);
  const [formData, setFormData] = useState({
    caliber: '',
    brand: '',
    grain: '',
    type: '',
    rounds: '',
    notes: '',
    images: [],
    totalCostPaid: '',
    roundsPurchased: '',
  });
  const [customCaliber, setCustomCaliber] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (isEdit) {
      const fetchAmmo = async () => {
        try {
          const response = await api.get(`/ammunition/${id}`);
          setAmmo(response.data);
          setFormData({
            caliber: response.data.caliber || '',
            brand: response.data.brand || '',
            grain: response.data.grain?.toString() || '',
            type: response.data.type || '',
            rounds: response.data.rounds?.toString() || '0',
            notes: response.data.notes || '',
            images: response.data.images || [],
            totalCostPaid: response.data.totalCostPaid?.toString() || '',
            roundsPurchased: response.data.roundsPurchased?.toString() || '',
          });
        } catch (error) {
          toast.error('Ammunition not found');
          navigate('/ammunition');
        } finally {
          setLoading(false);
        }
      };
      fetchAmmo();
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const { acceptedFiles, message } = validateImageUpload(files, formData.images.length);
    
    if (message) {
      toast.error(message);
    }
    
    if (acceptedFiles.length === 0) {
      e.target.value = '';
      return;
    }
    
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    if (selectedImage >= index && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.caliber) {
      toast.error('Please select a caliber');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        grain: formData.grain ? parseInt(formData.grain) : null,
        rounds: formData.rounds ? parseInt(formData.rounds) : 0,
        totalCostPaid: formData.totalCostPaid ? parseFloat(formData.totalCostPaid) : null,
        roundsPurchased: formData.roundsPurchased ? parseInt(formData.roundsPurchased) : null,
      };

      if (isEdit) {
        await api.put(`/ammunition/${id}`, payload);
        toast.success('Ammunition updated');
      } else {
        await api.post('/ammunition', payload);
        toast.success('Ammunition added');
      }
      navigate('/ammunition');
    } catch (error) {
      console.error('Error saving ammunition:', error);
      toast.error('Failed to save ammunition');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/ammunition/${id}`);
      toast.success('Ammunition deleted');
      navigate('/ammunition');
    } catch (error) {
      console.error('Error deleting ammunition:', error);
      toast.error('Failed to delete ammunition');
    } finally {
      setDeleting(false);
    }
  };

  const handleAdjust = async (amount) => {
    setAdjusting(true);
    try {
      const response = await api.post(`/ammunition/${id}/adjust`, { amount });
      setAmmo(response.data);
      setFormData(prev => ({ ...prev, rounds: response.data.rounds.toString() }));
      toast.success(`${amount > 0 ? 'Added' : 'Removed'} ${Math.abs(amount)} rounds`);
    } catch (error) {
      console.error('Error adjusting ammunition:', error);
      toast.error('Failed to adjust rounds');
    } finally {
      setAdjusting(false);
    }
  };

  const handleCustomAdjust = () => {
    const amount = parseInt(adjustAmount);
    if (!isNaN(amount) && amount !== 0) {
      handleAdjust(amount);
      setAdjustAmount('');
    }
  };

  // Calculate cost per round
  const calculateCostPerRound = () => {
    if (!ammo?.totalCostPaid || !ammo?.roundsPurchased || ammo.roundsPurchased <= 0) {
      return null;
    }
    return ammo.totalCostPaid / ammo.roundsPurchased;
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Ammunition Details' : 'Add Ammunition'}>
        <div className="animate-pulse space-y-6 max-w-2xl">
          <div className="h-10 bg-muted rounded w-full" />
          <div className="h-10 bg-muted rounded w-full" />
        </div>
      </Layout>
    );
  }

  // Detail view
  if (isDetail && ammo) {
    const costPerRound = calculateCostPerRound();
    
    return (
      <Layout title="Ammunition Details">
        <div className="max-w-3xl" data-testid="ammo-detail-page">
          <Button
            variant="ghost"
            onClick={() => navigate('/ammunition')}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Ammunition
          </Button>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Image Gallery - Fixed to use EntityImage for local storage */}
            <div className="space-y-4">
              <div className="aspect-square bg-card border border-border rounded-sm overflow-hidden">
                {ammo.images?.length > 0 ? (
                  <EntityImage
                    fileId={ammo.images[selectedImage]}
                    alt={ammo.caliber}
                    className="w-full h-full object-contain bg-muted"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <Boxes className="w-24 h-24 text-muted-foreground/30" />
                      </div>
                    }
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Boxes className="w-24 h-24 text-muted-foreground/30" />
                  </div>
                )}
              </div>

              {/* Thumbnails - Fixed to use EntityImage */}
              {ammo.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {ammo.images.map((imgId, idx) => (
                    <button
                      key={imgId || idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-16 h-16 flex-shrink-0 rounded-sm overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-secondary' : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <EntityImage 
                        fileId={imgId} 
                        alt="" 
                        className="w-full h-full object-cover"
                        fallback={<div className="w-full h-full bg-muted" />}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-accent/10 rounded-sm flex items-center justify-center">
                      <Boxes className="w-8 h-8 text-accent" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-foreground font-['Chivo']">
                        {ammo.caliber}
                      </h1>
                      <p className="text-muted-foreground">
                        {ammo.brand || 'Unknown brand'}
                        {ammo.type && ` • ${ammo.type}`}
                        {ammo.grain && ` • ${ammo.grain}gr`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/ammunition/${id}/edit`)}
                      className="rounded-sm"
                      data-testid="edit-btn"
                    >
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          data-testid="delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Ammunition</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this ammunition entry?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleting}
                          >
                            {deleting ? 'Deleting...' : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>

              {/* Round Counter */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-6">
                  Round Count
                </h3>
                
                <div className="flex items-center justify-center gap-4 mb-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAdjust(-50)}
                    disabled={adjusting || ammo.rounds < 50}
                    className="rounded-sm h-10 px-3"
                  >
                    -50
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAdjust(-10)}
                    disabled={adjusting || ammo.rounds < 10}
                    className="rounded-sm h-10 px-3"
                  >
                    -10
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleAdjust(-1)}
                    disabled={adjusting || ammo.rounds < 1}
                    className="rounded-sm h-10 w-10"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>

                  <div className="text-center px-6">
                    <span className="text-4xl font-mono font-bold text-foreground">
                      {ammo.rounds.toLocaleString()}
                    </span>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Rounds</p>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleAdjust(1)}
                    disabled={adjusting}
                    className="rounded-sm h-10 w-10"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAdjust(10)}
                    disabled={adjusting}
                    className="rounded-sm h-10 px-3"
                  >
                    +10
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAdjust(50)}
                    disabled={adjusting}
                    className="rounded-sm h-10 px-3"
                  >
                    +50
                  </Button>
                </div>

                {/* Custom adjustment */}
                <div className="flex items-center justify-center gap-3">
                  <Input
                    type="number"
                    placeholder="Custom amount (+ or -)"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    className="w-48 rounded-sm text-center"
                    data-testid="custom-adjust-input"
                  />
                  <Button
                    onClick={handleCustomAdjust}
                    disabled={adjusting || !adjustAmount}
                    className="rounded-sm"
                  >
                    Adjust
                  </Button>
                </div>
              </div>

              {/* Cost Tracking */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                  Cost Tracking
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  {/* Total Cost Paid */}
                  <div className="text-center p-3 bg-muted/30 rounded-sm">
                    <div className="flex items-center justify-center gap-1 text-2xl font-mono font-bold text-foreground">
                      <DollarSign className="w-5 h-5" />
                      {ammo.totalCostPaid != null ? ammo.totalCostPaid.toFixed(2) : '—'}
                    </div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Total Paid</p>
                  </div>
                  
                  {/* Rounds Purchased */}
                  <div className="text-center p-3 bg-muted/30 rounded-sm">
                    <div className="text-2xl font-mono font-bold text-foreground">
                      {ammo.roundsPurchased != null ? ammo.roundsPurchased.toLocaleString() : '—'}
                    </div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Purchased</p>
                  </div>
                  
                  {/* Cost Per Round */}
                  <div className="text-center p-3 bg-secondary/10 rounded-sm">
                    <div className="flex items-center justify-center gap-1 text-2xl font-mono font-bold text-secondary">
                      <DollarSign className="w-5 h-5" />
                      {costPerRound != null ? costPerRound.toFixed(3) : '—'}
                    </div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Per Round</p>
                  </div>
                </div>
                
                {(!ammo.totalCostPaid || !ammo.roundsPurchased) && (
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    Edit to add cost information
                  </p>
                )}
              </div>

              {/* Notes */}
              {ammo.notes && (
                <div className="bg-card border border-border rounded-sm p-6">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                    Notes
                  </h3>
                  <p className="text-foreground whitespace-pre-wrap">{ammo.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Form view (add/edit)
  return (
    <Layout title={isEdit ? 'Edit Ammunition' : 'Add Ammunition'}>
      <div className="max-w-2xl" data-testid="ammo-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/ammunition')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Ammunition
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Ammunition Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="caliber">Caliber *</Label>
                <Select value={COMMON_CALIBERS.includes(formData.caliber) ? formData.caliber : ''} onValueChange={(v) => handleSelectChange('caliber', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-caliber">
                    <SelectValue placeholder="Select caliber" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_CALIBERS.map(caliber => (
                      <SelectItem key={caliber} value={caliber}>{caliber}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!COMMON_CALIBERS.includes(formData.caliber) && formData.caliber && (
                  <div className="flex items-center gap-2 p-2 bg-secondary/10 rounded-sm mt-2">
                    <span className="text-sm font-medium">Custom: {formData.caliber}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, caliber: '' }))}
                      className="h-6 px-2 text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Or enter custom caliber"
                    value={customCaliber}
                    onChange={(e) => setCustomCaliber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (customCaliber.trim()) {
                          setFormData(prev => ({ ...prev, caliber: customCaliber.trim() }));
                          setCustomCaliber('');
                        }
                      }
                    }}
                    className="rounded-sm flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (customCaliber.trim()) {
                        setFormData(prev => ({ ...prev, caliber: customCaliber.trim() }));
                        setCustomCaliber('');
                      }
                    }}
                    disabled={!customCaliber.trim()}
                    className="rounded-sm"
                  >
                    Set
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  placeholder="e.g., Federal, Winchester"
                  className="rounded-sm"
                  data-testid="input-brand"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select value={formData.type || ''} onValueChange={(v) => handleSelectChange('type', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {AMMO_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="grain">Grain</Label>
                <Input
                  id="grain"
                  name="grain"
                  type="number"
                  value={formData.grain}
                  onChange={handleInputChange}
                  placeholder="e.g., 115, 124, 147"
                  className="rounded-sm"
                  data-testid="input-grain"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rounds">Current Inventory (Rounds on Hand)</Label>
              <Input
                id="rounds"
                name="rounds"
                type="number"
                min="0"
                value={formData.rounds}
                onChange={handleInputChange}
                placeholder="How many rounds you currently have"
                className="rounded-sm"
                data-testid="input-rounds"
              />
              <p className="text-xs text-muted-foreground">
                Your current count of rounds in stock
              </p>
            </div>
          </div>

          {/* Cost Tracking Section */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Cost Tracking (Optional)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enter your original purchase details to calculate cost per round. This is separate from your current inventory count above.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="totalCostPaid">Total Purchase Price ($)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="totalCostPaid"
                    name="totalCostPaid"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.totalCostPaid}
                    onChange={handleInputChange}
                    placeholder="e.g., 19.99"
                    className="rounded-sm pl-9"
                    data-testid="input-total-cost"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  What you paid for the original box/case
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="roundsPurchased">Rounds in Original Purchase</Label>
                <Input
                  id="roundsPurchased"
                  name="roundsPurchased"
                  type="number"
                  min="1"
                  value={formData.roundsPurchased}
                  onChange={handleInputChange}
                  placeholder="e.g., 50"
                  className="rounded-sm"
                  data-testid="input-rounds-purchased"
                />
                <p className="text-xs text-muted-foreground">
                  How many rounds came in the original purchase
                </p>
              </div>
            </div>

            {/* Cost per round preview */}
            {formData.totalCostPaid && formData.roundsPurchased && parseInt(formData.roundsPurchased) > 0 && (
              <div className="flex items-center justify-center gap-2 p-3 bg-secondary/10 rounded-sm">
                <span className="text-sm text-muted-foreground">Cost per round:</span>
                <span className="text-lg font-mono font-bold text-secondary">
                  ${(parseFloat(formData.totalCostPaid) / parseInt(formData.roundsPurchased)).toFixed(3)}
                </span>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Additional Info
            </h3>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any notes..."
                rows={3}
                className="rounded-sm"
                data-testid="input-notes"
              />
            </div>
          </div>

          {/* Images */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo']">
                Images
              </h3>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {getImageCountDisplay(formData.images.length)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Upload images for identification (box, packaging, or rounds)
            </p>
            <div className="grid grid-cols-4 gap-4">
              {formData.images.map((img, idx) => (
                <div key={idx} className="relative aspect-square bg-muted rounded-sm overflow-hidden group">
                  <EntityImage fileId={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {canAddMoreImages(formData.images.length) && (
                <label className="aspect-square border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-secondary transition-colors">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground">Upload</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="input-images"
                  />
                </label>
              )}
            </div>
            {formData.images.length >= MAX_IMAGES_PER_ITEM && (
              <p className="text-xs text-muted-foreground">
                Maximum {MAX_IMAGES_PER_ITEM} photos reached. Remove existing photos to add new ones.
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#2E4A7D] hover:bg-[#3B5998] text-white rounded-sm btn-tactical"
              data-testid="submit-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Add Ammunition'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/ammunition')}
              className="rounded-sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
